import { create } from "zustand";

/**
 * Minimal Zustand store for the aim trainer (one store per app, hard rule).
 *
 *   idle → running (20s, hit targets) → done
 *
 * One target at a time at a random spot; tapping it scores a hit and respawns it
 * elsewhere. Position is normalized 0..1 so it maps to any play-area size. The countdown
 * timer lives in the component; the store just holds display state.
 */
export const ROUND_MS = 20_000;

export type AimPhase = "idle" | "running" | "done";

export interface Target {
  /** normalized [0,1] within the play area. */
  x: number;
  y: number;
  /** bumped each spawn so React remounts the target (retrigger pop animation). */
  id: number;
}

interface AimState {
  phase: AimPhase;
  hits: number;
  timeLeftMs: number;
  target: Target | null;
  /** best hits this session (localStorage-backed all-time best). */
  best: number;

  start: () => void;
  hit: () => void;
  tick: (msLeft: number) => void;
  finish: () => void;
  reset: () => void;
}

const BEST_KEY = "kp:aim:best";
function loadBest(): number {
  try {
    return Number.parseInt(localStorage.getItem(BEST_KEY) ?? "0", 10) || 0;
  } catch {
    return 0;
  }
}
function saveBest(n: number): void {
  try {
    localStorage.setItem(BEST_KEY, String(n));
  } catch {
    /* blocked storage — fine */
  }
}

// Spawn within an inset so targets never clip the play-area edges (target radius ~margin).
const INSET = 0.08;
function randomTarget(id: number): Target {
  return {
    x: INSET + Math.random() * (1 - 2 * INSET),
    y: INSET + Math.random() * (1 - 2 * INSET),
    id,
  };
}

export const useAimStore = create<AimState>((set, get) => ({
  phase: "idle",
  hits: 0,
  timeLeftMs: ROUND_MS,
  target: null,
  best: loadBest(),

  start: () =>
    set({ phase: "running", hits: 0, timeLeftMs: ROUND_MS, target: randomTarget(0) }),

  hit: () => {
    const { phase, hits, target } = get();
    if (phase !== "running") return;
    const nextHits = hits + 1;
    set({ hits: nextHits, target: randomTarget((target?.id ?? 0) + 1) });
  },

  tick: (msLeft) => set({ timeLeftMs: Math.max(0, msLeft) }),

  finish: () => {
    const { hits, best } = get();
    const nextBest = Math.max(best, hits);
    if (nextBest > best) saveBest(nextBest);
    set({ phase: "done", target: null, timeLeftMs: 0, best: nextBest });
  },

  reset: () =>
    set({ phase: "idle", hits: 0, timeLeftMs: ROUND_MS, target: null, best: loadBest() }),
}));
